import {
  Participation,
  ParticipationStatus,
  SessionStatus,
  StudyType,
  useParticipations,
} from '@app/api';
import { useMemo } from 'react';

const isSessionNotClosed = (participation: Participation) =>
  participation?.session?.status !== SessionStatus.CLOSED;

const isSessionLive = (participation: Participation) =>
  participation?.session?.status === SessionStatus.LIVE;

const compareByRejectAtOrAppointment = (a: Participation, b: Participation) => {
  const appointmentDateA = a.appointment?.date
    ? new Date(a.appointment.date)
    : null;
  const appointmentDateB = b.appointment?.date
    ? new Date(b.appointment.date)
    : null;
  const dateA = a.automaticallyRejectAt || appointmentDateA;
  const dateB = b.automaticallyRejectAt || appointmentDateB;
  if (!(dateA && dateB)) {
    return 0;
  }
  return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
};

const compareByRecentAppointment = (a: Participation, b: Participation) => {
  if (!(a.appointment?.date && b.appointment?.date)) {
    return 0;
  }
  return (
    new Date(b.appointment.date).getTime() -
    new Date(a.appointment.date).getTime()
  );
};

const compareByEmergency = (a: Participation, b: Participation) => {
  if (a.session?.studySubject === 'B2C' && b.session?.studySubject === 'B2B') {
    return 1;
  }
  if (a.session?.studySubject === 'B2B' && b.session?.studySubject === 'B2C') {
    return -1;
  }
  if (a.session?.emergency && b.session?.emergency)
    if (a.session.emergency > b.session.emergency) {
      return -1;
    } else {
      return 1;
    }
  return 0;
};

export type ParticipationColumn =
  | 'past'
  | 'confirmed'
  | 'invited'
  | 'accepted'
  | 'applied';

const columnConfigs: Record<
  ParticipationColumn,
  {
    status: ParticipationStatus[];
    sort?: (a: Participation, b: Participation) => number;
    filter?: (p: Participation) => boolean;
  }
> = {
  past: {
    status: [
      ParticipationStatus.PAYED,
      ParticipationStatus.PARTICIPATED,
      ParticipationStatus.PROCESSING_PAYMENT,
      ParticipationStatus.PAYMENT_REQUESTED,
    ],
    sort: compareByRecentAppointment,
  },
  confirmed: {
    status: [ParticipationStatus.BOOKED, ParticipationStatus.IN_PROGRESS],
    sort: compareByRejectAtOrAppointment,
    filter: isSessionNotClosed,
  },
  accepted: {
    status: [ParticipationStatus.ACCEPTED],
    sort: compareByEmergency,
    filter: isSessionNotClosed,
  },
  applied: {
    status: [
      ParticipationStatus.APPLIED,
      ParticipationStatus.DECLINED,
      ParticipationStatus.OUT_OF_QUOTA,
    ],
    filter: isSessionNotClosed,
  },
  invited: {
    status: [ParticipationStatus.INVITED],
    sort: compareByEmergency,
    filter: isSessionLive,
  },
};

export const useParticipationsByColumns = () => {
  const { data: participations } = useParticipations();

  return useMemo(() => {
    const result = {
      confirmed: [] as Participation[],
    } as Record<ParticipationColumn, Participation[]>;

    for (const key in columnConfigs) {
      const column = key as ParticipationColumn;
      const columnConfig = columnConfigs[column];
      const { status, filter, sort } = columnConfig;
      result[column] = [];

      participations?.forEach((p) => {
        if (status.includes(p.status)) {
          if (!filter || filter(p)) {
            if (
              p.session?.studyType === StudyType.UNMODERATED &&
              p.status === ParticipationStatus.ACCEPTED
            ) {
              result['confirmed'].push(p);
            } else {
              result[column].push(p);
            }
          }
        }
      });

      if (sort) {
        result[column].sort(sort);
      }
    }
    return result;
  }, [participations]);
};
